/**
 * Announce an election — officers only (write on governance.elections).
 * Mode picker shows the statutory reality inline: SCEA-run is the law for
 * committee elections (Sec. 73CB); self-run in-app ballots are for small
 * societies under the Sec. 154B-20 relaxations.
 * Wired: POST /api/elections.
 */
import { router } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

import { radius, space } from '@bero/tokens';
import { goBack } from '@/features/account-admin/bits';
import { Icon } from '@/features/account-admin/icons';
import { useT } from '@/i18n';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Button, Chip, Field, Input, Screen, TopBar } from '@/ui';

const MODES: { key: 'SCEA' | 'SELF_RUN'; label: string; rule: string }[] = [
  { key: 'SCEA', label: 'SCEA-run (statutory)',
    rule: 'Sec. 73CB, bye-law 115(e) — the State Co-operative Election Authority conducts the poll; Bero coordinates the timeline, nominations and records the result.' },
  { key: 'SELF_RUN', label: 'Self-run ballot',
    rule: 'Sec. 154B-20 relaxations for small societies — members vote in the app; eligibility gates (active member, non-defaulter) apply to every ballot.' },
];

export default function NewElectionScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const [mode, setMode] = React.useState<'SCEA' | 'SELF_RUN'>('SCEA');
  const [title, setTitle] = React.useState('');
  const [term, setTerm] = React.useState('');
  const [seats, setSeats] = React.useState('');
  const [reserved, setReserved] = React.useState('');
  const [nomClose, setNomClose] = React.useState('');
  const [voteDate, setVoteDate] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  const meta = MODES.find((m) => m.key === mode) ?? MODES[0];

  const submit = async () => {
    setErr(null);
    if (title.trim().length < 3) { setErr(t('eln_err_title')); return; }
    const n = Number(seats);
    if (!Number.isInteger(n) || n < 1 || n > 50) { setErr(t('eln_err_seats')); return; }
    for (const [v, labelKey] of [[nomClose, 'eln_f_nomclose'], [voteDate, 'eln_f_votedate']] as const) {
      if (v.trim() && !/^\d{4}-\d{2}-\d{2}$/.test(v.trim())) {
        setErr(t('eln_err_date', { label: t(labelKey) })); return;
      }
    }
    setBusy(true);
    try {
      await api.post<unknown>('/api/elections', {
        title: title.trim(), termLabel: term.trim(), seatsTotal: n, mode,
        reservedNote: reserved.trim(),
        nominationClose: nomClose.trim() || null, votingDate: voteDate.trim() || null,
      });
      router.replace('/elections');
    } catch (e) {
      setErr((e as { error?: string })?.error ?? t('eln_err_generic'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Screen scroll={false}>
      <TopBar title={t('eln_title')} onBack={goBack} />
      <Screen padded style={{ paddingTop: space.s4 }}>
        <View style={{ flexDirection: 'row', gap: space.s2, flexWrap: 'wrap' }}>
          {MODES.map((m) => (
            <Chip key={m.key} label={m.label} active={mode === m.key} onPress={() => setMode(m.key)} />
          ))}
        </View>
        <View style={{ borderRadius: radius.lg, backgroundColor: colors.canvas1, padding: space.s3, marginTop: space.s3 }}>
          <View style={{ flexDirection: 'row', gap: 6, alignItems: 'flex-start' }}>
            <Icon name="lock" size={13} color={colors.accent700} />
            <AppText variant="bodySm" style={{ color: colors.accent700, flex: 1 }}>{meta.rule}</AppText>
          </View>
        </View>

        <View style={{ gap: space.s4, marginTop: space.s4, marginBottom: space.s6 }}>
          <Field label={t('eln_f_title')}>
            <Input value={title} onChangeText={setTitle} placeholder={t('eln_ph_title')} maxLength={200} />
          </Field>
          <View style={{ flexDirection: 'row', gap: space.s3 }}>
            <View style={{ flex: 1 }}>
              <Field label={t('eln_f_seats')}>
                <Input value={seats} onChangeText={setSeats} placeholder={t('eln_ph_seats')} keyboardType="number-pad" />
              </Field>
            </View>
            <View style={{ flex: 1.4 }}>
              <Field label={t('eln_f_term')}>
                <Input value={term} onChangeText={setTerm} placeholder="2026-2031" maxLength={20} />
              </Field>
            </View>
          </View>
          <Field label={t('eln_f_reserved')} help="Sec. 154B-20 — e.g. seats reserved for women / SC-ST">
            <Input value={reserved} onChangeText={setReserved} placeholder="e.g. 1 seat reserved for women" maxLength={250} />
          </Field>
          <View style={{ flexDirection: 'row', gap: space.s3 }}>
            <View style={{ flex: 1 }}>
              <Field label={t('eln_f_nomclose')}>
                <Input value={nomClose} onChangeText={setNomClose} placeholder="2026-08-01" />
              </Field>
            </View>
            <View style={{ flex: 1 }}>
              <Field label={t('eln_f_votedate')}>
                <Input value={voteDate} onChangeText={setVoteDate} placeholder="2026-08-15" />
              </Field>
            </View>
          </View>
          {err ? <AppText variant="bodySm" weight="500" style={{ color: colors.danger500 }}>{err}</AppText> : null}
          <Button title={busy ? t('eln_announcing') : t('eln_title')} disabled={busy} onPress={() => { void submit(); }} />
        </View>
      </Screen>
    </Screen>
  );
}
